import { useMutation } from 'react-query';
import axios from 'axios';
import { TerminalAppearanceAPI } from '../../../constants/API';
import { queryClient } from '../../../queryClient';
import { commonHeaders } from '../../../constants/HeadersAPI';
import { useContext } from 'react';
import { AlertContext } from '../../../context/AlertProvider';
import { AlertType } from '../../../types/enums';
import { defaultSuccessMessage } from '../../../constants/alertText';
import {
  TerminalAppearancePutPayload,
  TerminalLogoSettings,
  TerminalTabloSettings,
  TerminalTabloSettingsFromBackend
} from '../../../types/Admin/TerminalAppearanceTypes';

const DEFAULT_LOGO_FIT_MODE = 'scale';
const DEFAULT_LOGO_SCALE = 100;
const DEFAULT_LOGO_POSITION = 50;

/** Все поля логотипа заданы явно (числа и строки), чтобы в JSON не было undefined и бэкенд сохранял режим/масштаб/позицию. */
const normalizeLogoForBackend = (
  logo: TerminalLogoSettings | null | undefined
): TerminalLogoSettings | null => {
  if (logo == null) return null;
  return {
    url: logo.url ?? null,
    fitMode: logo.fitMode ?? DEFAULT_LOGO_FIT_MODE,
    scale: Number.isFinite(Number(logo.scale)) ? Number(logo.scale) : DEFAULT_LOGO_SCALE,
    positionX: Number.isFinite(Number(logo.positionX)) ? Number(logo.positionX) : DEFAULT_LOGO_POSITION,
    positionY: Number.isFinite(Number(logo.positionY)) ? Number(logo.positionY) : DEFAULT_LOGO_POSITION
  };
};

const denormalizeTabloForBackend = (
  tablo: TerminalTabloSettings | TerminalTabloSettingsFromBackend | null | undefined
): TerminalTabloSettingsFromBackend | null | undefined => {
  if (!tablo) return tablo;
  if (!('elements' in tablo) || !tablo.elements) {
    return { ...tablo, logo: normalizeLogoForBackend(tablo.logo) } as TerminalTabloSettingsFromBackend;
  }
  const { elements, ...rest } = tablo;
  return {
    ...rest,
    logo: normalizeLogoForBackend(rest.logo),
    header: elements.header ?? null,
    video: elements.video ?? null,
    title: elements.title ?? null,
    queue: elements.queue ?? null,
    takenTickets: elements.takenTickets ?? null,
    ticker: elements.ticker ?? null,
    slider: elements.slider ?? null,
    weatherWidget: elements.weatherWidget ?? null
  };
};

export const usePutTerminalAppearance = () => {
  const context = useContext(AlertContext);

  const { mutate: updateTerminalAppearance, isLoading: isUpdateTerminalAppearanceLoading } =
    useMutation(
      ['TerminalAppearanceUpdate'],
      (data: TerminalAppearancePutPayload) => {
        const { queueIds, ...settings } = data;
        return axios.put(
          TerminalAppearanceAPI.updateAppearance,
          {
            ...settings,
            tablo: denormalizeTabloForBackend(settings.tablo),
            ...(queueIds != null && queueIds.length > 0 ? { queueIds } : {})
          },
          commonHeaders()
        );
      },
      {
        onSuccess: () => {
          Promise.all([
            queryClient.invalidateQueries('TerminalAppearance'),
            queryClient.invalidateQueries('QueueTerminalAppearance')
          ]).then(() =>
            context?.setAlert({ type: AlertType.Success, message: defaultSuccessMessage })
          );
        }
      }
    );

  return { updateTerminalAppearance, isUpdateTerminalAppearanceLoading };
};
